import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PieController,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import axios from 'axios';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PieController,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = memo(() => {
  const [aggregatedSales, setAggregatedSales] = useState({
    dailyTotal: 0,
    weeklyTotal: 0,
    monthlyTotal: 0,
    yearlyTotal: 0,
    dailyCount: 0,
    weeklyCount: 0,
    monthlyCount: 0,
    yearlyCount: 0
  });
  const [salesByBranch, setSalesByBranch] = useState([]);
  const [salesByCashier, setSalesByCashier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoized fetch function with error handling
  const fetchSalesData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use Promise.all to fetch data in parallel
      const [aggregatedResponse, groupedResponse] = await Promise.all([
        axios.get('/api/sales/salesTime'),
        axios.get('/api/sales/salesBranch')
      ]);

      setAggregatedSales(aggregatedResponse.data);
      setSalesByBranch(groupedResponse.data.salesByBranch);
      setSalesByCashier(groupedResponse.data.salesByCashier);
    } catch (err) {
      console.error('Failed to fetch sales data:', err);
      setError('Failed to fetch sales data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalesData();

    // Set up polling for data updates (optional)
    const intervalId = setInterval(fetchSalesData, 30000); // Update every 30 seconds
    
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [fetchSalesData]);

  // Memoized number formatter
  const formatNumber = useCallback((num) => {
    if (num < 1000) return num;
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    return (num / 1000).toFixed(1) + 'K';
  }, []);

  // Memoized chart data generators
  const generateChartData = useMemo(() => ({
    bar: (data, label) => ({
      labels: data.map(item => item._id),
      datasets: [{
        label,
        data: data.map(item => item.total),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(5, 38, 255, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(75, 192, 192, 0.4)',
        hoverBorderColor: 'rgba(75, 192, 192, 1)',
      }],
    }),
    pie: (data) => ({
      labels: data.map(item => item._id),
      datasets: [{
        data: data.map(item => item.total),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#FF9F40',
          '#4BC0C0',
        ],
        hoverBackgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#FF9F40',
          '#4BC0C0',
        ],
      }],
    }),
    line: (data) => ({
      labels: data.map(item => item._id),
      datasets: [{
        label: 'Sales by Branch',
        data: data.map(item => item.total),
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(5, 38, 255, 1)',
        borderWidth: 2,
        tension: 0.1,
      }],
    })
  }), []);

  // Memoized chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => `$${context.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${formatNumber(value)}`
        }
      }
    }
  }), [formatNumber]);

  // Loading and error states
  if (loading) return (
    <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
      <div className="tw-text-blue-800 tw-text-xl">Loading dashboard data...</div>
    </div>
  );

  if (error) return (
    <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
      <div className="tw-text-red-500 tw-text-xl">{error}</div>
      <button 
        onClick={fetchSalesData}
        className="tw-ml-4 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700"
      >
        Retry
      </button>
    </div>
  );

  return (
    <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen">
      {/* Summary Cards */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-mb-8">
        {[
          { label: "Today's sales", value: aggregatedSales.dailyTotal, count: aggregatedSales.dailyCount },
          { label: "Weekly sales", value: aggregatedSales.weeklyTotal, count: aggregatedSales.weeklyCount },
          { label: "Monthly sales", value: aggregatedSales.monthlyTotal, count: aggregatedSales.monthlyCount },
          { label: "Yearly sales", value: aggregatedSales.yearlyTotal, count: aggregatedSales.yearlyCount }
        ].map((item, index) => (
          <div key={index} className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-flex tw-flex-col tw-items-center">
            <p className="tw-text-red-500 tw-font-bold tw-text-2xl tw-mb-1">
              ${formatNumber(item.value)}
            </p>
            <p className="tw-text-gray-600 tw-mb-2">{item.label}</p>
            <p className="tw-text-sm tw-text-gray-500">{item.count} transactions</p>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-8">
        {/* Pie Chart */}
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
          <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales Distribution by Branch</h2>
          <div className="tw-h-64">
            <Pie
              data={generateChartData.pie(salesByBranch)}
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: { display: true, text: 'Sales by Branch' }
                }
              }}
            />
          </div>
        </div>

        {/* Bar Charts */}
        <div className="tw-grid tw-grid-cols-1 tw-gap-8">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
            <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales by Branch</h2>
            <div className="tw-h-64">
              <Bar
                data={generateChartData.bar(salesByBranch, 'Sales by Branch')}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    title: { display: true, text: 'Sales by Branch' }
                  }
                }}
              />
            </div>
          </div>

          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
            <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales by Cashier</h2>
            <div className="tw-h-64">
              <Bar
                data={generateChartData.bar(salesByCashier, 'Sales by Cashier')}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    title: { display: true, text: 'Sales by Cashier' }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Line Chart */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-mb-8">
        <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales Trend by Branch</h2>
        <div className="tw-h-96">
          <Line
            data={generateChartData.line(salesByBranch)}
            options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                title: { display: true, text: 'Sales Trend by Branch' }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default AdminDashboard;